import { Controller } from "@hotwired/stimulus"
import {
  Chart,
  LineController,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Legend,
  Tooltip
} from "chart.js"

Chart.register(LineController, CategoryScale, LinearScale, PointElement, LineElement, Legend, Tooltip)

const AXIS_COLORS = {
  frappe: "rgba(59, 130, 246, 1)",       // bleu
  volley: "rgba(34, 197, 94, 1)",        // vert
  lob: "rgba(249, 115, 22, 1)",          // orange
  service: "rgba(139, 92, 246, 1)",      // violet
  deplacement: "rgba(236, 72, 153, 1)",  // rose
  tactique: "rgba(6, 182, 212, 1)"       // cyan
}

const AXIS_LABELS = {
  frappe: "Frappe",
  volley: "Volée",
  lob: "Lob",
  service: "Service",
  deplacement: "Déplacement",
  tactique: "Tactique"
}

export default class extends Controller {
  static targets = ["canvas", "periodBtn"]
  static values = {
    timeline: Array,
    axes: { type: Array, default: ["frappe", "volley", "lob", "service", "deplacement", "tactique"] },
    period: { type: Number, default: 6 }
  }

  connect() {
    this.renderChart()
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy()
      this.chart = null
    }
  }

  setPeriod(event) {
    const months = parseInt(event.currentTarget.dataset.months)
    this.periodValue = months

    // Update active button style
    this.periodBtnTargets.forEach(btn => {
      const isActive = parseInt(btn.dataset.months) === months
      btn.classList.toggle("bg-blue-600", isActive)
      btn.classList.toggle("text-white", isActive)
      btn.classList.toggle("bg-gray-200", !isActive)
      btn.classList.toggle("text-gray-700", !isActive)
    })

    this.renderChart()
  }

  renderChart() {
    if (this.chart) {
      this.chart.destroy()
    }

    const cutoff = new Date()
    cutoff.setMonth(cutoff.getMonth() - this.periodValue)

    const filtered = this.timelineValue.filter(entry => new Date(entry.date) >= cutoff)
    if (filtered.length === 0) return

    const labels = filtered.map(entry => {
      const d = new Date(entry.date)
      return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short" })
    })

    const datasets = this.axesValue.map(axis => {
      const color = AXIS_COLORS[axis] || "rgba(107, 114, 128, 1)"
      return {
        label: AXIS_LABELS[axis] || axis,
        data: filtered.map(entry => entry[axis] ?? null),
        borderColor: color,
        backgroundColor: color.replace(", 1)", ", 0.1)"),
        borderWidth: 2,
        pointRadius: 3,
        pointHoverRadius: 5,
        tension: 0.3,
        spanGaps: true
      }
    })

    this.chart = new Chart(this.canvasTarget, {
      type: "line",
      data: { labels, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: "index",
          intersect: false
        },
        scales: {
          y: {
            min: 0,
            max: 5,
            ticks: { stepSize: 1 },
            grid: { color: "rgba(148, 163, 184, 0.2)" }
          },
          x: {
            grid: { display: false }
          }
        },
        plugins: {
          legend: {
            position: "bottom",
            labels: {
              font: { size: 11 },
              usePointStyle: true,
              pointStyle: "circle",
              padding: 16
            }
          },
          tooltip: {
            callbacks: {
              label: (ctx) => `${ctx.dataset.label}: ${ctx.raw !== null ? ctx.raw + '/5' : 'N/A'}`
            }
          }
        }
      }
    })
  }
}
